import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { getQuestions, submitAssessment } from '../api/api';
import { PageContainer, Card, Button } from '../components/ui';

export default function Assessment() {
  const { organizationId } = useParams();
  const { t } = useLanguage();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    getQuestions()
      .then((data) => {
        setQuestions(data);
        const initial = {};
        data.forEach((q) => (initial[q.id] = null));
        setAnswers(initial);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const allAnswered = questions.length > 0 && questions.every((q) => answers[q.id] !== null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allAnswered) return;
    setSubmitting(true);
    setError('');
    try {
      const answerList = Object.entries(answers).map(([questionId, answerValue]) => ({
        questionId: Number(questionId),
        answerValue,
      }));
      const result = await submitAssessment(organizationId, answerList);
      navigate(`/org/${organizationId}/report/${result.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <PageContainer>
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0ea5e9]" />
          </div>
        </PageContainer>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageContainer>
        <h1 className="text-2xl font-bold text-[#0f172a] dark:text-slate-100 mb-2">{t.securityAssessment}</h1>
        <p className="text-[#475569] dark:text-slate-400 mb-8">
          {t.assessmentDesc}
        </p>

        {error && (
          <div className="bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/30 p-4 rounded-[10px] mb-6">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {questions.map((q, idx) => (
            <Card key={q.id} className="p-6">
              <p className="text-[#0f172a] dark:text-slate-100 font-semibold mb-3">
                {idx + 1}. {q.questionText}
              </p>
              <p className="text-sm text-[#475569] dark:text-slate-400 mb-4">
                {t.weight}: {q.weight} | {t.category}: {q.category || t.general}
              </p>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer text-[#0f172a] dark:text-slate-200">
                  <input
                    type="radio"
                    name={`q-${q.id}`}
                    checked={answers[q.id] === true}
                    onChange={() => handleChange(q.id, true)}
                    className="w-4 h-4 accent-[#0ea5e9]"
                  />
                  <span>{t.yes}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-[#0f172a] dark:text-slate-200">
                  <input
                    type="radio"
                    name={`q-${q.id}`}
                    checked={answers[q.id] === false}
                    onChange={() => handleChange(q.id, false)}
                    className="w-4 h-4 accent-[#0ea5e9]"
                  />
                  <span>{t.no}</span>
                </label>
              </div>
            </Card>
          ))}

          <div className="flex justify-end">
            <Button type="submit" variant="primary" disabled={!allAnswered || submitting}>
              {submitting ? t.submitting : t.submitAssessment}
            </Button>
          </div>
        </form>
      </PageContainer>
    </Layout>
  );
}
